import type { OrderDetails } from '../services/orders.service';

export interface ReuseEligibility {
  /** Show the "Reuse Container" button (import-side initiates a street turn). */
  canReuse: boolean;
  /** Show the "Undo Reuse Container" button (already reused; reversible). */
  canUndoReuse: boolean;
  /** Show the "View Reuse Options" button (export-side read-only browse). */
  canViewReuse: boolean;
  /** Reason the Reuse button should be disabled (offhire / hold / food-grade),
   * surfaced as the button's title. Null when no blocking flag is set. */
  disabledReason: string | null;
}

// Mirrors v1 lib_editrecord.pl around line 831 and the drawer's `reuseEligibility`
// memo. Pulled into a shared util so OrderReuseActions (used from /pro and the
// drawer) computes the same conditions in one place.
export function computeReuseEligibility(
  orderDetails: OrderDetails | null | undefined,
  streetTurnOpportunitiesCount: number,
): ReuseEligibility {
  const empty: ReuseEligibility = {
    canReuse: false,
    canUndoReuse: false,
    canViewReuse: false,
    disabledReason: null,
  };
  if (!orderDetails) return empty;

  const od = orderDetails;
  const origLeg = od.legs.find((l) => l.legnum === od.originateLeg);
  const termLeg = od.legs.find((l) => l.legnum === od.terminateLeg);

  const isImport = od.moveType === 'IMPORT';
  const isChainedReuse =
    ['INBOUND', 'OUTBOUND', 'DOMESTIC'].includes(od.moveType) &&
    (od.reuseImportPro ?? 0) > 0;

  // "In motion" gate (V1 INC-8518): the originate leg must have departed.
  // House moves ride placeholder driver "000" and never get the departure
  // stamped (tracing can show OUTGATED while the leg field stays zero), so
  // a placeholder-driver leg is exempt from the departure requirement.
  const origLegInMotion =
    Boolean(origLeg?.origDepartDateTime) || origLeg?.driver === '000';

  const canReuse =
    od.scac !== 'IDMC' &&
    // Reefer containers are never street-turned — hide the button outright.
    !(od.equipType ?? '').toUpperCase().includes('REEFER') &&
    (isImport || isChainedReuse) &&
    (od.terminateLeg ?? 0) !== 0 &&
    origLegInMotion &&
    !termLeg?.destArrivalDateTime &&
    !(isImport && (od.reuseContainerPro ?? 0) > 0);

  let disabledReason: string | null = null;
  if (od.offhire === 'Y') disabledReason = 'Container is off-hire';
  else if (od.equipHold === 'Y') disabledReason = 'Equipment hold flag is set';
  else if (od.streetTurnHold === 'Y') disabledReason = 'Street-turn hold flag is set';
  else if (od.foodGrade === 'Y') disabledReason = 'Food-grade containers cannot be street-turned';

  const canUndoReuse =
    (od.reuseContainerPro ?? 0) > 0 &&
    ['EXPORT', 'RAIL TO PORT', 'INBOUND', 'OUTBOUND', 'DOMESTIC', 'REPO'].includes(od.moveType) &&
    !origLeg?.origDepartDateTime &&
    !origLeg?.destArrivalDateTime &&
    !origLeg?.driver;

  // Exports (and rail-to-port) don't initiate reuse, but dispatchers want
  // visibility into the candidate imports already flagged in
  // street_turn_opportunity. View-only — drawer opens without a Reuse action.
  const canViewReuse =
    ['EXPORT', 'RAIL TO PORT'].includes(od.moveType) &&
    streetTurnOpportunitiesCount > 0;

  return { canReuse, canUndoReuse, canViewReuse, disabledReason };
}